import React, { type ReactNode } from 'react';
import { DiaryActionCards } from '@/components/DiaryActionCards';
import { DiaryDayLessons } from '@/components/DiaryDayLessons';
import { DateCarousel } from '@/components/DiaryHeader/DateCarousel';
import { MobileDiaryHeader } from '@/components/DiaryHeader/MobileDiaryHeader';
import { DiaryPreschoolPlans } from '@/components/DiaryPreschoolPlans';
import { ProfileDropdown } from '@/components/ProfileDropdown';
import { ProfileStatusBanner } from '@/components/ProfileStatusBanner';
import { PageSpinner } from '@/components/Spinner/PageSpinner';
import type { DiaryEntry, PreschoolEducationPlan } from '@/types/diary';
import { isSameApiDate, toApiDate } from '@/utils/date';
import { getPlansInPeriod } from '@/utils/diary';

interface Props {
  entries: DiaryEntry[];
  preschoolPlans: PreschoolEducationPlan[];
  areEntriesLoading: boolean;
  browsableDates: Date[];
  isCurrentStudyYear: boolean;
  selectedDate: Date;
  minDate: Date;
  maxDate: Date;
  adBanner?: ReactNode;
  onDateChange: (date: Date) => void;
}

export const MobileDiary: React.FC<Props> = ({
  entries,
  preschoolPlans,
  areEntriesLoading,
  browsableDates,
  isCurrentStudyYear,
  selectedDate,
  minDate,
  maxDate,
  adBanner,
  onDateChange,
}) => {
  const selectedApiDate = toApiDate(selectedDate);
  const selectedEntry = entries.find((entry) =>
    isSameApiDate(entry.date, selectedApiDate),
  );
  const dayPlans = getPlansInPeriod({
    plans: preschoolPlans,
    from: selectedApiDate,
    to: selectedApiDate,
  });

  return (
    <div className="flex flex-1 flex-col md:hidden">
      <div className="px-4 pt-[calc(12px+env(safe-area-inset-top))] pb-3.5">
        <ProfileDropdown />
      </div>

      <ProfileStatusBanner className="pt-2 pb-2" />

      <div className="flex gap-3 px-4 py-2">
        <DiaryActionCards />
      </div>

      <div className="sticky top-0 z-20 bg-background">
        <MobileDiaryHeader
          isCurrentStudyYear={isCurrentStudyYear}
          selectedDate={selectedDate}
          minDate={minDate}
          maxDate={maxDate}
          onDateChange={onDateChange}
        />
        <DateCarousel
          dates={browsableDates}
          value={selectedDate}
          entries={entries}
          onChange={onDateChange}
        />
      </div>

      <div className="flex min-h-[calc(100svh_-_194px_-_env(safe-area-inset-bottom))] flex-1 flex-col pt-3">
        {areEntriesLoading && (
          <div className="flex flex-1 items-center justify-center">
            <PageSpinner className="size-[20px] text-subtle-foreground" />
          </div>
        )}

        {!areEntriesLoading && (
          <div className="flex flex-1 flex-col rounded-2xl bg-card px-4 pt-5 pb-4">
            <DiaryDayLessons entry={selectedEntry} />

            <DiaryPreschoolPlans plans={dayPlans} />

            {adBanner}
          </div>
        )}
      </div>
    </div>
  );
};
